// JIRA-style 전체 화면 이슈 상세 페이지 (라우트: /projects/:key/issues/:issueId)
// 외부 진입(마이 홈, 채팅 배지, 멘션 등)에서 사용되는 풀 페이지 레이아웃.
// 이슈 리스트/보드 내 빠른 미리보기는 기존 IssueDetailPanel (사이드 패널) 유지.
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useOutletContext, Link } from 'react-router-dom';
import { ArrowLeft, Trash2, Tag, Calendar, User, AlertCircle, Hash, Link2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useIssue, useAddComment, useTransitionIssue, useUpdateIssue, useDeleteIssue, useActivityLogs } from '../hooks/useIssues';
import { useProjectIssueTypes } from '../hooks/useProjectIssueTypes';
import { useFieldConfig } from '../hooks/useIssueTypeFieldConfig';
import { useResolvedWorkflowStatuses } from '../hooks/useResolvedWorkflow';
import { usePanelContext } from '../contexts/PanelContext';
import { resolveIssueTypeConfig } from '../constants/issueTypeConfig';
import { PRIORITY_BADGE } from '../components/issue/IssueListView';
import StatusTransitionDropdown from '../components/issue/StatusTransitionDropdown';
import IssueAttachmentSection from '../components/issue/IssueAttachmentSection';
import IssueDetailFields from '../components/issue/IssueDetailFields';
import DeleteIssueModal from '../components/issue/DeleteIssueModal';
import SourceMessageMeta from '../components/chat/SourceMessageMeta';
import Spinner from '../components/ui/Spinner';
import type { ProjectDetail } from '../services/projectService';
import Avatar from '../components/common/Avatar';
import { useProfileModal } from '../components/common/ProfileModalProvider';
import type { ProfileColorKey } from '@maios/shared';

const PRIORITIES: Array<'BLOCKER' | 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW'> = [
  'BLOCKER', 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW',
];

function formatDate(iso: string | null) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('ko-KR', { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatDateTime(iso: string) {
  const d = new Date(iso);
  return d.toLocaleString('ko-KR', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

interface RailItemProps {
  icon?: React.ReactNode;
  label: string;
  children: React.ReactNode;
}

function RailItem({ icon, label, children }: RailItemProps) {
  return (
    <div className="flex items-start gap-2 py-2">
      <div className="flex items-center gap-1.5 w-24 shrink-0 pt-1">
        {icon}
        <span className="text-sm font-medium text-gray-500">{label}</span>
      </div>
      <div className="flex-1 min-w-0 text-sm text-gray-800">{children}</div>
    </div>
  );
}

export default function IssueDetailFullPage() {
  const { issueId } = useParams<{ issueId: string }>();
  const navigate = useNavigate();
  const { project } = useOutletContext<{ project: ProjectDetail }>();
  const { openProfileModal } = useProfileModal();

  const projectId = project.id;
  const projectKey = project.projectKey;
  const members = project.members;
  const projectWorkflow = project.workflow;

  const { openDetailPanel } = usePanelContext();
  const { data: issue, isLoading, error } = useIssue(projectId, issueId ?? '');
  const { data: activities } = useActivityLogs(projectId, issueId ?? '');
  const { data: projectIssueTypes } = useProjectIssueTypes(projectId);
  const transitionMutation = useTransitionIssue(projectId);
  const addCommentMutation = useAddComment(projectId);
  const updateMutation = useUpdateIssue(projectId);
  const deleteMutation = useDeleteIssue(projectId);
  const { data: fieldConfig } = useFieldConfig((issue as { issueTypeId?: string } | undefined)?.issueTypeId);
  const resolvedWf = useResolvedWorkflowStatuses(projectId, issue?.issueType, projectWorkflow);

  const [commentText, setCommentText] = useState('');
  const [showDelete, setShowDelete] = useState(false);
  const [tab, setTab] = useState<'comments' | 'activity'>('comments');

  // 인라인 편집 상태
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState('');

  useEffect(() => {
    if (issue) {
      setTitleDraft(issue.title);
      setDescriptionDraft(issue.description ?? '');
    }
  }, [issue]);

  const handleTitleSave = useCallback(() => {
    if (!issue) return;
    const trimmed = titleDraft.trim();
    if (!trimmed || trimmed === issue.title) {
      setEditingTitle(false);
      setTitleDraft(issue.title);
      return;
    }
    updateMutation.mutate(
      { issueId: issue.id, data: { title: trimmed } },
      { onSuccess: () => setEditingTitle(false), onError: () => toast.error('제목 저장 실패') },
    );
  }, [issue, titleDraft, updateMutation]);

  const handleDescriptionSave = useCallback(() => {
    if (!issue) return;
    const next = descriptionDraft;
    if (next === (issue.description ?? '')) { setEditingDescription(false); return; }
    updateMutation.mutate(
      { issueId: issue.id, data: { description: next || null } },
      { onSuccess: () => setEditingDescription(false), onError: () => toast.error('설명 저장 실패') },
    );
  }, [issue, descriptionDraft, updateMutation]);

  const handleFieldUpdate = useCallback(
    (field: string, value: unknown) => {
      if (!issue) return;
      updateMutation.mutate(
        { issueId: issue.id, data: { [field]: value } as Parameters<typeof updateMutation.mutate>[0]['data'] },
        { onError: () => toast.error('필드 저장 실패') },
      );
    },
    [issue, updateMutation],
  );

  const handleAddComment = useCallback(() => {
    if (!issue || !commentText.trim()) return;
    addCommentMutation.mutate(
      { issueId: issue.id, content: commentText.trim() },
      {
        onSuccess: () => setCommentText(''),
        onError: () => toast.error('코멘트 작성 실패'),
      },
    );
  }, [issue, commentText, addCommentMutation]);

  const handleTransition = useCallback(
    (statusId: string) => {
      if (!issue) return;
      transitionMutation.mutate(
        { issueId: issue.id, statusId },
        { onError: () => toast.error('상태 변경 실패') },
      );
    },
    [issue, transitionMutation],
  );

  const handleDelete = useCallback(() => {
    if (!issue) return;
    deleteMutation.mutate(issue.id, {
      onSuccess: () => {
        toast.success('작업이 삭제되었습니다.');
        navigate(`/projects/${projectKey}/issues`);
      },
      onError: () => toast.error('삭제 실패'),
    });
  }, [issue, deleteMutation, navigate, projectKey]);

  if (isLoading) {
    return <div className="flex items-center justify-center h-64"><Spinner /></div>;
  }
  if (error || !issue) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-2">
        <p className="text-sm text-gray-500">작업을 불러올 수 없습니다.</p>
        <Link to={`/projects/${projectKey}/issues`} className="text-sm text-primary-600 hover:underline">작업 목록으로 돌아가기</Link>
      </div>
    );
  }

  const typeConfig = resolveIssueTypeConfig(issue.issueTypeDef ?? null, issue.issueType);
  const priorityBadge = PRIORITY_BADGE[issue.priority];

  return (
    <div className="flex flex-col h-full">
      {/* 상단 헤더 */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={() => navigate(`/projects/${projectKey}/issues`)}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
            aria-label="작업 목록으로"
          >
            <ArrowLeft className="size-4" />
          </button>
          <div className="flex items-center gap-2">
            <typeConfig.Icon className="size-4" style={{ color: typeConfig.hexColor }} />
            <span className="text-sm font-medium text-gray-500">{issue.issueId}</span>
            <span className="text-sm text-gray-400">·</span>
            <span className="text-sm text-gray-600">{issue.issueTypeDef?.name ?? issue.issueType}</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <StatusTransitionDropdown
            projectId={projectId}
            issueId={issue.id}
            currentStatusName={issue.workflowStatus.name}
            currentStatusColor={issue.workflowStatus.color}
            onTransition={handleTransition}
            disabled={transitionMutation.isPending}
          />
          <button
            onClick={() => setShowDelete(true)}
            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
            aria-label="삭제"
            title="삭제"
          >
            <Trash2 className="size-4" />
          </button>
        </div>
      </div>

      {/* 본문 — 2 column */}
      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 p-6">
          {/* === Main 좌측 === */}
          <div className="space-y-4">
            {/* 제목 */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              {editingTitle ? (
                <input
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onBlur={handleTitleSave}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.nativeEvent.isComposing) { e.preventDefault(); handleTitleSave(); }
                    if (e.key === 'Escape') { setEditingTitle(false); setTitleDraft(issue.title); }
                  }}
                  autoFocus
                  maxLength={300}
                  className="w-full text-2xl font-semibold text-gray-900 outline-none border-b-2 border-primary-500 pb-1"
                />
              ) : (
                <h1
                  className="text-2xl font-semibold text-gray-900 cursor-text hover:bg-gray-50 rounded px-1 -mx-1"
                  onClick={() => setEditingTitle(true)}
                >
                  {issue.title}
                </h1>
              )}
            </div>

            {/* 출처 메시지 (CHG-123) */}
            {issue.sourceMessage && (
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <SourceMessageMeta sourceMessage={issue.sourceMessage} />
              </div>
            )}

            {/* 설명 */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">설명</h2>
              {editingDescription ? (
                <div>
                  <textarea
                    value={descriptionDraft}
                    onChange={(e) => setDescriptionDraft(e.target.value)}
                    autoFocus
                    rows={Math.min(20, Math.max(6, descriptionDraft.split('\n').length + 1))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded outline-none focus:ring-1 focus:ring-primary-500 resize-y"
                    placeholder="설명을 입력하세요"
                  />
                  <div className="flex gap-2 mt-2 justify-end">
                    <button
                      onClick={() => { setEditingDescription(false); setDescriptionDraft(issue.description ?? ''); }}
                      className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded"
                    >
                      취소
                    </button>
                    <button
                      onClick={handleDescriptionSave}
                      disabled={updateMutation.isPending}
                      className="px-3 py-1.5 text-sm text-white bg-primary-600 rounded hover:bg-primary-700 disabled:opacity-50"
                    >
                      저장
                    </button>
                  </div>
                </div>
              ) : issue.description ? (
                <div
                  className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed cursor-text hover:bg-gray-50 rounded p-2 -m-2"
                  onClick={() => setEditingDescription(true)}
                >
                  {issue.description}
                </div>
              ) : (
                <button
                  onClick={() => setEditingDescription(true)}
                  className="text-sm text-gray-400 italic hover:text-gray-600"
                >
                  설명을 추가하려면 클릭
                </button>
              )}
            </div>

            {/* 첨부 */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">첨부</h2>
              <IssueAttachmentSection
                projectId={projectId}
                issueId={issue.id}
                attachments={(issue as { issueAttachments?: unknown[] }).issueAttachments as Parameters<typeof IssueAttachmentSection>[0]['attachments'] ?? []}
              />
            </div>

            {/* 커스텀 필드 (CHG-053) */}
            {fieldConfig && fieldConfig.sections.some((s) => s.fields.some((f) => !f.field.isSystem && f.visibility !== 'HIDDEN')) && (
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">추가 필드</h2>
                <IssueDetailFields
                  issue={issue as unknown as Record<string, unknown>}
                  sections={fieldConfig.sections.filter((s) => s.fields.some((f) => !f.field.isSystem))}
                  isEditing={false}
                />
              </div>
            )}

            {/* 코멘트 + 활동 탭 */}
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="flex border-b border-gray-200">
                <button
                  onClick={() => setTab('comments')}
                  className={`px-4 py-2 text-sm font-medium ${tab === 'comments' ? 'text-primary-600 border-b-2 border-primary-600' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  코멘트 ({issue.comments.length})
                </button>
                <button
                  onClick={() => setTab('activity')}
                  className={`px-4 py-2 text-sm font-medium ${tab === 'activity' ? 'text-primary-600 border-b-2 border-primary-600' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  활동 ({activities?.length ?? 0})
                </button>
              </div>

              {tab === 'comments' ? (
                <div className="p-4 space-y-4">
                  {/* 코멘트 입력 */}
                  <div className="flex gap-2">
                    <textarea
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      placeholder="코멘트를 입력하세요"
                      rows={2}
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded outline-none focus:ring-1 focus:ring-primary-500 resize-y"
                    />
                    <button
                      onClick={handleAddComment}
                      disabled={!commentText.trim() || addCommentMutation.isPending}
                      className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded hover:bg-primary-700 disabled:opacity-50 self-start"
                    >
                      등록
                    </button>
                  </div>
                  {/* 코멘트 리스트 */}
                  <div className="space-y-3">
                    {issue.comments.map((c) => (
                      <div key={c.id} className="flex gap-2">
                        <Avatar
                          size="sm"
                          userId={c.author.id}
                          name={c.author.name}
                          avatarUrl={c.author.avatarUrl}
                          displayColor={c.author.displayColor as ProfileColorKey | null | undefined}
                          firstName={c.author.firstName}
                          onClick={() => openProfileModal(c.author.id)}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900">{c.author.name}</span>
                            <span className="text-sm text-gray-400">{formatDateTime(c.createdAt)}</span>
                          </div>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap mt-0.5">{c.content}</p>
                        </div>
                      </div>
                    ))}
                    {issue.comments.length === 0 && (
                      <p className="text-sm text-gray-400 italic text-center py-4">첫 코멘트를 남겨보세요</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="p-4 space-y-2">
                  {(activities ?? []).map((a) => (
                    <div key={a.id} className="text-sm text-gray-600 flex items-start gap-2">
                      <span className="text-gray-400 shrink-0">{formatDateTime(a.createdAt)}</span>
                      <span>
                        <span className="font-medium text-gray-700">{a.user.name}</span>
                        {' '}
                        <span className="text-gray-500">{a.action}</span>
                      </span>
                    </div>
                  ))}
                  {(!activities || activities.length === 0) && (
                    <p className="text-sm text-gray-400 italic text-center py-4">활동 내역이 없습니다</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* === Sidebar 우측 === */}
          <aside className="space-y-4">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">세부 정보</h2>
              <div className="divide-y divide-gray-100">
                <RailItem icon={<User className="size-3.5 text-gray-400" />} label="담당자">
                  <select
                    value={issue.assignee?.id ?? ''}
                    onChange={(e) => handleFieldUpdate('assigneeId', e.target.value || null)}
                    className="w-full text-sm bg-transparent border-none outline-none cursor-pointer hover:bg-gray-50 rounded px-1 -mx-1"
                  >
                    <option value="">미지정</option>
                    {members.map((m) => (
                      <option key={m.userId} value={m.userId}>{m.user.name}</option>
                    ))}
                  </select>
                </RailItem>
                <RailItem icon={<User className="size-3.5 text-gray-400" />} label="보고자">
                  <span>{issue.reporter.name}</span>
                </RailItem>
                <RailItem icon={<AlertCircle className="size-3.5 text-gray-400" />} label="우선순위">
                  <span className={`inline-block px-2 py-0.5 rounded text-sm font-medium ${priorityBadge?.class ?? ''} mr-1`}>
                    {priorityBadge?.label ?? issue.priority}
                  </span>
                  <select
                    value={issue.priority}
                    onChange={(e) => handleFieldUpdate('priority', e.target.value)}
                    className="text-sm bg-transparent border-none outline-none cursor-pointer hover:bg-gray-50 rounded px-1"
                  >
                    {PRIORITIES.map((p) => (<option key={p} value={p}>{p}</option>))}
                  </select>
                </RailItem>
                <RailItem icon={<Hash className="size-3.5 text-gray-400" />} label="유형">
                  <select
                    value={issue.issueType}
                    onChange={(e) => {
                      const def = projectIssueTypes?.issueTypes.find((t) => t.name === e.target.value);
                      handleFieldUpdate('issueTypeId', def?.issueTypeId);
                    }}
                    className="w-full text-sm bg-transparent border-none outline-none cursor-pointer hover:bg-gray-50 rounded px-1 -mx-1"
                  >
                    {(projectIssueTypes?.issueTypes ?? []).map((t) => (
                      <option key={t.issueTypeId} value={t.name}>{t.name}</option>
                    ))}
                  </select>
                </RailItem>
                <RailItem icon={<Calendar className="size-3.5 text-gray-400" />} label="시작일">
                  <input
                    type="date"
                    value={issue.startDate ? issue.startDate.slice(0, 10) : ''}
                    onChange={(e) => handleFieldUpdate('startDate', e.target.value || null)}
                    className="w-full text-sm bg-transparent border-none outline-none cursor-pointer hover:bg-gray-50 rounded px-1 -mx-1"
                  />
                </RailItem>
                <RailItem icon={<Calendar className="size-3.5 text-gray-400" />} label="마감일">
                  <input
                    type="date"
                    value={issue.dueDate ? issue.dueDate.slice(0, 10) : ''}
                    onChange={(e) => handleFieldUpdate('dueDate', e.target.value || null)}
                    className="w-full text-sm bg-transparent border-none outline-none cursor-pointer hover:bg-gray-50 rounded px-1 -mx-1"
                  />
                </RailItem>
                <RailItem icon={<Tag className="size-3.5 text-gray-400" />} label="라벨">
                  {issue.labels.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {issue.labels.map((l) => (
                        <span key={l} className="px-1.5 py-0.5 text-sm bg-gray-100 text-gray-700 rounded">
                          {l}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-gray-400 italic">없음</span>
                  )}
                </RailItem>
                {issue.storyPoints != null && (
                  <RailItem label="스토리 포인트">
                    <span>{issue.storyPoints}</span>
                  </RailItem>
                )}
              </div>
            </div>

            {/* 상위·하위 작업 */}
            {(issue.parentIssue || (issue.childIssues && issue.childIssues.length > 0)) && (
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">계층</h2>
                {issue.parentIssue && (
                  <div className="text-sm text-gray-500 mb-2">
                    <span className="block mb-1">상위 작업</span>
                    <button
                      type="button"
                      onClick={() => openDetailPanel('issue', issue.parentIssue!.id)}
                      className="text-sm text-primary-600 hover:underline text-left"
                    >
                      {issue.parentIssue.issueId} {issue.parentIssue.title}
                    </button>
                  </div>
                )}
                {issue.childIssues && issue.childIssues.length > 0 && (
                  <div className="text-sm text-gray-500">
                    <span className="block mb-1">하위 작업 ({issue.childIssues.length})</span>
                    <ul className="space-y-1">
                      {issue.childIssues.map((c) => (
                        <li key={c.id}>
                          <button
                            type="button"
                            onClick={() => openDetailPanel('issue', c.id)}
                            className="text-sm text-primary-600 hover:underline text-left"
                          >
                            {c.issueId} {c.title}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* 추적성 링크 */}
            {(issue.linkedRequirement || issue.linkedSpec || issue.linkedTestCase) && (
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                  <Link2 className="size-3.5" /> 연결
                </h2>
                <ul className="space-y-1 text-sm">
                  {issue.linkedRequirement && (
                    <li className="flex items-center gap-1">
                      <span className="text-sm text-gray-400 w-12 shrink-0">요구사항</span>
                      <span className="font-medium text-primary-600">{issue.linkedRequirement.reqId}</span>
                      <span className="text-gray-600 truncate">{issue.linkedRequirement.title}</span>
                    </li>
                  )}
                  {issue.linkedSpec && (
                    <li className="flex items-center gap-1">
                      <span className="text-sm text-gray-400 w-12 shrink-0">명세</span>
                      <span className="font-medium text-primary-600">{issue.linkedSpec.functionCode}</span>
                      <span className="text-gray-600 truncate">{issue.linkedSpec.title}</span>
                    </li>
                  )}
                  {issue.linkedTestCase && (
                    <li className="flex items-center gap-1">
                      <span className="text-sm text-gray-400 w-12 shrink-0">테스트</span>
                      <span className="font-medium text-primary-600">{issue.linkedTestCase.tcId}</span>
                      <span className="text-gray-600 truncate">{issue.linkedTestCase.title}</span>
                    </li>
                  )}
                </ul>
              </div>
            )}

            {/* 메타데이터 */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">기록</h2>
              <div className="text-sm text-gray-500 space-y-1">
                <div>생성: {formatDateTime(issue.createdAt)} · {issue.reporter.name}</div>
                <div>수정: {formatDate(issue.updatedAt)}</div>
              </div>
            </div>

            {/* 워크플로우 검증 정보 */}
            {resolvedWf.isOverride && (
              <p className="text-sm text-gray-400 italic">이 유형에는 별도 워크플로우가 적용됩니다</p>
            )}
          </aside>
        </div>
      </div>

      {/* 삭제 모달 */}
      <DeleteIssueModal
        isOpen={showDelete}
        issueKey={issue.issueId}
        onClose={() => setShowDelete(false)}
        onConfirm={async () => { handleDelete(); }}
      />
    </div>
  );
}
